export class OPDAPI {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  async getContactHistory(contactId, queryParams = {}) {
    const queryString = new URLSearchParams(queryParams).toString();
    return this.apiClient.get(`/api/v1/opd/history/contact/${contactId}${queryString ? `?${queryString}` : ''}`);
  }

  async createContactHistory(historyData) {
    return this.apiClient.post('/api/v1/opd/history/contact', historyData);
  }

  async updateContactHistory(updateData) {
    return this.apiClient.put('/api/v1/opd/history/contact', updateData);
  }

  async deleteContactHistory(deleteData) {
    return this.apiClient.delete('/api/v1/opd/history/contact/each-delete', {
      data: deleteData
    });
  }

  async getHistoryParams() {
    return this.apiClient.get('/api/v1/opd/history/params');
  }

  async getContactHistoryAuditLogs(contactId, page = 1, limit = 15) {
    return this.apiClient.get(`/api/v1/opd/history/contact/${contactId}/audit-logs?page=${page}&limit=${limit}`);
  }

  async getPatientInvestigations(patientCode) {
    return this.apiClient.get(`/api/v1/opd/investigations/patient/${patientCode}`);
  }

  async createPatientInvestigation(investigationData) {
    return this.apiClient.post('/api/v1/opd/investigations', investigationData);
  }

  async updatePatientInvestigation(investigationId, investigationData) {
    return this.apiClient.put(`/api/v1/opd/investigations/${investigationId}`, investigationData);
  }

  async deletePatientInvestigation(investigationId) {
    return this.apiClient.delete(`/api/v1/opd/investigations/${investigationId}`);
  }

  // Prescription methods
  async createPrescription(prescriptionData) {
    return this.apiClient.post('/api/v1/opd/prescription/create', prescriptionData);
  }

  async getPrescriptionById(prescriptionId) {
    return this.apiClient.get(`/api/v1/opd/prescription/${prescriptionId}`);
  }

  async getPrescriptions(patientCode, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiClient.get(`/api/v1/opd/prescription/patient/${patientCode}${queryString ? `?${queryString}` : ''}`);
  }

  async searchPrescriptions(searchParams = {}) {
    const queryString = new URLSearchParams(searchParams).toString();
    return this.apiClient.get(`/api/v1/opd/prescription/search${queryString ? `?${queryString}` : ''}`);
  }

  async searchPatientPrescriptions(patientCode, searchParams = {}) {
    const queryString = new URLSearchParams(searchParams).toString();
    return this.apiClient.get(`/api/v1/opd/prescription/patient/${patientCode}/search${queryString ? `?${queryString}` : ''}`);
  }

  async updatePrescription(prescriptionId, prescriptionData) {
    return this.apiClient.put(`/api/v1/opd/prescription/${prescriptionId}`, prescriptionData);
  }

  async deletePrescription(prescriptionId) {
    return this.apiClient.delete(`/api/v1/opd/prescription/${prescriptionId}`);
  }

  async getPrescriptionDetailsByCode(prescriptionCode) {
    return this.apiClient.get(`/api/v1/opd/prescription/code/${prescriptionCode}/details`);
  }

  async managePrescriptionComponents(componentData) {
    return this.apiClient.post('/api/v1/opd/prescription/manage/components', componentData);
  }

  async deletePrescriptionComponent(deleteData) {
    return this.apiClient.delete('/api/v1/opd/prescription/component/delete', {
      data: deleteData
    });
  }

  async publishPrescription(prescriptionId, userId) {
    return this.apiClient.put(`/api/v1/opd/prescription/${prescriptionId}/publish`, {
      userId: userId
    });
  }
  async createObstetricRecord(formData) {
    return this.apiClient.post("/api/v1/opd/history/obsteric", formData);
  }

  async getObstetricRecord(patientCode) {
    return this.apiClient.get(`/api/v1/opd/history/obsteric/${patientCode}`);
  }

  async deleteObstetricRecord(deleteData) {
    return this.apiClient.delete('/api/v1/opd/history/obsteric/delete', {
      data: deleteData
    });
  }

  async createMiscarriageRecord(formData) {
    return this.apiClient.post("/api/v1/opd/history/miscarriage", formData);
  }
  
  async updateMiscarriageHistory(updateData) {
    return this.apiClient.put('/api/v1/opd/history/miscarriage', updateData);
  }
  
  async getMiscarriageRecords(patientCode) {
    return this.apiClient.get(`/api/v1/opd/history/miscarriage/${patientCode}`);
  }

  async deleteMiscarriageRecord(deleteData) {
    return this.apiClient.delete(`/api/v1/opd/history/miscarriage/delete`, {
      data: deleteData
    });
  }

  async deleteMenstrualHistory(deleteData) {
    return this.apiClient.delete('/api/v1/opd/history/menstrual/delete', {
      data: deleteData
    });
  }

  // Prescription Scan File methods
  async uploadPrescriptionScanFile(prescriptionId, file, description) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', description || '');
    formData.append('created_by', 1); // TODO: Get from auth context
    
    return this.apiClient.post(`/api/v1/opd/prescription/${prescriptionId}/scan-file/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async getPrescriptionScanFiles(prescriptionId) {
    return this.apiClient.get(`/api/v1/opd/prescription/${prescriptionId}/scan-files`);
  }

  async deletePrescriptionScanFile(scanFileId, deletedBy) {
    return this.apiClient.delete(`/api/v1/opd/prescription/scan-file/${scanFileId}`, {
      data: { deleted_by: deletedBy }
    });
  }

  // ── AI Prescription Scan ─────────────────────────────────────────────────

  async uploadPrescriptionAiScan(files, prescriptionId) {
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];
    fileArray.forEach(f => formData.append('files', f));
    if (prescriptionId) formData.append('prescription_id', prescriptionId);
    return this.apiClient.post('/api/v1/opd/prescription/ai-scan/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  async getPrescriptionAiScanStatus(jobRef) {
    return this.apiClient.get(`/api/v1/opd/prescription/ai-scan/status/${jobRef}`);
  }

  async getPrescriptionAiScanJobs(prescriptionId) {
    return this.apiClient.get(`/api/v1/opd/prescription/ai-scan/prescription/${prescriptionId}/jobs`);
  }

  async linkAiScanToPrescription(jobRef, prescriptionId) {
    return this.apiClient.put(`/api/v1/opd/prescription/ai-scan/${jobRef}/link-prescription/${prescriptionId}`);
  }

  // Follow-up methods
  async createFollowup(data) {
    return this.apiClient.post('/api/v1/opd/followup/create', data);
  }

  async updateFollowup(followupId, data) {
    return this.apiClient.put(`/api/v1/opd/followup/${followupId}`, data);
  }

  async getFollowupById(followupId) {
    return this.apiClient.get(`/api/v1/opd/followup/${followupId}`);
  }

  async listFollowups(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiClient.get(`/api/v1/opd/followup/list${queryString ? `?${queryString}` : ''}`);
  }

  async getFollowupsByContact(contactId) {
    return this.apiClient.get(`/api/v1/opd/followup/contact/${contactId}`);
  }

  async getLastDoctorForContact(contactId) {
    return this.apiClient.get(`/api/v1/opd/followup/contact/${contactId}/last-doctor`);
  }

  async adminListFollowups(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.apiClient.get(`/api/v1/opd/followup/admin/list${queryString ? `?${queryString}` : ''}`);
  }
}
